import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdminNotifications, sendAdminNotification } from '../../api';
import { PageHeader, Table, Pagination, EmptyState, TableSkeleton } from '../../components/ui';
import { formatDateTime } from '../../utils/format';
import { Bell } from 'lucide-react';
import toast from 'react-hot-toast';

export default function NotificationsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({ title: '', body: '', audience: 'users', type: 'promotion' });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-notifs', page],
    queryFn: () => getAdminNotifications({ page, limit: 20 }).then((r) => r.data),
  });
  const rows = data?.data || [];
  const pagination = data?.pagination;

  const send = useMutation({
    mutationFn: () => sendAdminNotification(form),
    onSuccess: (r) => {
      toast.success(r.data?.message || 'Sent');
      setForm({ title: '', body: '', audience: 'users', type: 'promotion' });
      qc.invalidateQueries({ queryKey: ['admin-notifs'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to send'),
  });

  return (
    <div className="p-6">
      <PageHeader title="Push notifications" subtitle="Broadcast in-app notifications to users or astrologers" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card p-5 lg:col-span-1">
          <h3 className="font-semibold mb-3">Send new</h3>
          <label className="block text-xs text-gray-500 mb-1">Audience</label>
          <select className="input mb-3" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
            <option value="users">All users</option>
            <option value="astrologers">All astrologers</option>
            <option value="all">Everyone</option>
          </select>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select className="input mb-3" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="promotion">Promotion</option>
            <option value="system">System</option>
            <option value="payment">Payment</option>
            <option value="consultation">Consultation</option>
          </select>
          <input className="input mb-3" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea className="input min-h-[100px] mb-3" placeholder="Message" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <button disabled={send.isPending || !form.title.trim() || !form.body.trim()} onClick={() => send.mutate()} className="btn-primary w-full">Send notification</button>
        </div>
        <div className="card overflow-hidden lg:col-span-2">
          {isLoading ? <div className="p-5"><TableSkeleton rows={8} cols={4} /></div>
            : rows.length === 0 ? <EmptyState message="No notifications yet" icon={<Bell size={32} />} />
            : (
              <Table>
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="table-th">Title</th><th className="table-th">Audience</th><th className="table-th">Type</th><th className="table-th">Date</th>
                </tr></thead>
                <tbody>
                  {rows.map((n: any) => (
                    <tr key={n._id} className="table-row">
                      <td className="table-td">
                        <p className="text-sm font-medium">{n.title}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[280px]">{n.body}</p>
                      </td>
                      <td className="table-td text-xs capitalize">{n.userType}</td>
                      <td className="table-td text-xs capitalize">{n.type}</td>
                      <td className="table-td text-xs">{formatDateTime(n.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          {pagination?.pages > 1 && <div className="p-4 border-t"><Pagination page={page} pages={pagination.pages} total={pagination.total} limit={20} onPageChange={setPage} /></div>}
        </div>
      </div>
    </div>
  );
}
